import React from 'react';
import {JobApplicationStatus} from '../../../models/job-application';
import ProfilePicture from '../../../components/profile-picture';
import {Tag, TagSize, TagVariant} from '../../../components/tag';
import {ApplicantStatusBadge} from './applicant-status-badge';
import {AuraScoreBar} from './aura-score-bar';

const CARD_BASE_CLASS =
  'relative w-full min-w-[200px] max-w-xs p-5 rounded-2xl shadow-sm flex flex-col items-center gap-3 border border-gray-200 transition-shadow duration-200 hover:shadow-lg';

const TOP_SKILLS_MAX = 5;

function getAvatarBorderClass(scorePercent: number): string {
  if (scorePercent >= 80) return 'ring-2 ring-green-500';
  if (scorePercent >= 60) return 'ring-2 ring-blue-500';
  return 'ring-2 ring-gray-300';
}

function isRequiredSkill(skillLabel: string, requiredSkillNames: string[]): boolean {
  const normalized = skillLabel.trim().toLowerCase();
  return requiredSkillNames.some(r => r.trim().toLowerCase() === normalized);
}

export interface ApplicantCardSkill {
  id: string;
  label: string;
}

interface ApplicantCardProps {
  name: string;
  email: string;
  phone: string;
  imageUrl?: string;
  auraScore: number;
  appliedDate: string;
  headline?: string;
  location?: string;
  skills?: ApplicantCardSkill[];
  requiredSkillNames?: string[];
  rank?: number;
  isNew?: boolean;
  status?: JobApplicationStatus;
  onButtonClick?: () => void;
}

export const ApplicantCard: React.FC<ApplicantCardProps> = ({
  name,
  imageUrl,
  auraScore,
  appliedDate,
  headline,
  location,
  skills = [],
  requiredSkillNames = [],
  rank,
  isNew = false,
  status = JobApplicationStatus.APPLIED,
  onButtonClick,
}) => {
  const scorePercent = auraScore > 0 && auraScore <= 1 ? Math.round(auraScore * 100) : Math.round(auraScore);
  const avatarRingClass = getAvatarBorderClass(scorePercent);
  const topSkills = skills.slice(0, TOP_SKILLS_MAX);

  const requiredSkillsMet =
    requiredSkillNames.length > 0 ? topSkills.filter(s => isRequiredSkill(s.label, requiredSkillNames)).length : undefined;
  const requiredSkillsTotal = requiredSkillNames.length > 0 ? requiredSkillNames.length : undefined;
  const missingSkillNames =
    requiredSkillNames.length > 0 ? requiredSkillNames.filter(r => !topSkills.some(s => isRequiredSkill(s.label, [r]))) : undefined;

  const primaryButtonClass = 'w-full px-4 py-2.5 text-white text-sm font-medium rounded-full transition bg-blue-600 hover:bg-blue-700';

  const statusStyles: Record<JobApplicationStatus, {buttonText: string}> = {
    [JobApplicationStatus.APPLIED]: {buttonText: 'View Profile'},
    [JobApplicationStatus.INVITED]: {buttonText: 'View Aura'},
    [JobApplicationStatus.REJECTED]: {buttonText: 'View Feedback'},
    [JobApplicationStatus.ACCEPTED]: {buttonText: 'Shortlisted'},
    [JobApplicationStatus.WITHDRAWN]: {buttonText: 'Withdrawn'},
  };
  const buttonLabel = statusStyles[status]?.buttonText ?? 'View Profile';

  return (
    <div className={`${CARD_BASE_CLASS} bg-white`}>
      {rank != null && (
        <div className="absolute top-3 left-3">
          <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded bg-gray-800 px-1.5 text-xs font-semibold text-white">
            #{rank}
          </span>
        </div>
      )}
      <ApplicantStatusBadge status={status} isNew={isNew} />
      <div className={`inline-flex rounded-full ${avatarRingClass}`}>
        <ProfilePicture name={name} url={imageUrl} />
      </div>
      <div className="flex flex-col items-center gap-0.5 text-center w-full">
        <h3 className="text-gray-900 font-semibold text-base">{name}</h3>
        {headline && (
          <p className="text-gray-600 text-sm line-clamp-1 w-full" title={headline}>
            {headline}
          </p>
        )}
        {location && <p className="text-gray-500 text-xs">{location}</p>}
      </div>
      {topSkills.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center w-full">
          {topSkills.map((s, i) => {
            const required = isRequiredSkill(s.label, requiredSkillNames);
            return (
              <Tag key={s.id || i} variant={required ? TagVariant.Primary : TagVariant.Default} size={TagSize.SM}>
                {s.label}
              </Tag>
            );
          })}
        </div>
      )}
      {status !== JobApplicationStatus.INVITED && (
        <AuraScoreBar
          auraScore={scorePercent}
          requiredSkillsMet={requiredSkillsMet}
          requiredSkillsTotal={requiredSkillsTotal}
          missingSkillNames={missingSkillNames}
        />
      )}
      <p className="text-gray-500 text-xs text-center w-full">Applied on {appliedDate}</p>
      {onButtonClick && (
        <button onClick={onButtonClick} className={primaryButtonClass}>
          {buttonLabel}
        </button>
      )}
    </div>
  );
};
